// Rigorous ADP/ACP nondiscrimination limit math, modeled on the actual test in
// 26 CFR 1.401(k)-2 (ADP) and 1.401(m)-2 (ACP).
//
// The statutory limit on the HCE group average is the GREATER of:
//   (1) 1.25 x the NHCE group average, OR
//   (2) the lesser of (2 x the NHCE group average) and (NHCE average + 2 points).
// A plan passes if the HCE average does not exceed that greater-of limit.
//
// The engine previously expressed this as a piecewise limit (2n for n<=2, n+2 for
// 2<n<=8, else 1.25n). That closed form is exactly equal to the greater-of rule at
// every n, so this module is behavior-preserving; it just makes the rule explicit,
// citable, and independently testable, and adds helpers (binding branch, evaluate)
// the remediation copy reuses.
//
// This remains a MODELED screen, not a formal test. It uses one plan-year
// capped-compensation definition, the current-year testing method, and the app's
// non-excludable population. It does not perform prior-year testing, restructuring
// or disaggregation, the multiple-use test, QNEC/QMAC corrections, recharacterization,
// gap-period income, or any plan-document election. It is not a substitute for the
// plan administrator's formal ADP/ACP test.

export const ADP_ACP_CITATION = '26 CFR 1.401(k)-2 (ADP) / 1.401(m)-2 (ACP)'

// The permitted HCE-group average given the NHCE-group average, both expressed as
// percentages of compensation. Returns the greater-of limit described above.
export function adpAcpLimit(nhceAveragePercent) {
  const nhce = Math.max(0, Number(nhceAveragePercent) || 0)
  const factorBranch = nhce * 1.25
  const spreadBranch = Math.min(nhce * 2, nhce + 2)
  return Math.max(factorBranch, spreadBranch)
}

// Which branch is binding, for explanatory copy. Never claims a formal result.
export function adpAcpBindingBranch(nhceAveragePercent) {
  const nhce = Math.max(0, Number(nhceAveragePercent) || 0)
  return nhce * 1.25 >= Math.min(nhce * 2, nhce + 2) ? '1.25x' : '2x-or-plus-2'
}

// Evaluate the HCE group against the limit. `available` mirrors the engine's rule
// that both an HCE and an NHCE rate are required to run the screen at all.
export function evaluateAdpAcp({ hceAverage, nhceAverage, hceCount, nhceCount }) {
  const available = Number(hceCount) > 0 && Number(nhceCount) > 0
  const limit = available ? adpAcpLimit(nhceAverage) : null
  const hce = Math.max(0, Number(hceAverage) || 0)
  return {
    available,
    limit,
    margin: available ? limit - hce : null,
    bindingBranch: available ? adpAcpBindingBranch(nhceAverage) : null,
    status: !available
      ? 'unavailable'
      : hce <= limit
        ? 'within-basic-threshold'
        : 'over-basic-threshold',
  }
}
